import { WagerFacade } from '@amwager/wager/data-access';
// Angular
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { fetch } from '@nx/angular';
import { Platform } from '@ionic/angular';

// Capacitor
import { App } from '@capacitor/app';

// Third party
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { map, take } from 'rxjs';
import { Smartlook } from '@awesome-cordova-plugins/smartlook/ngx';

// Local library
import {
  CommonFacade,
  HOME_DIRECTORY,
  PusherService,
  SessionService,
  ToteAccountAPIService,
  UserAPIService,
  JurisdictionAPIService,
  SMARTLOOK
} from '@amwager/data-access';
import { INACTIVE, GUEST, GUEST_USER, ILoggedInUserModel, Web, TO_DECRYPT } from '@amwager/util-ts';

// Local file
import * as AuthActions from './auth.actions';
import { AuthFacade } from './auth.facade';
import { AccountFacade } from '@amwager/account/data-access';
import { FundingFacade } from '@amwager/funding/data-access';
import { TicketFacade } from '@amwager/ticket/data-access';



@Injectable()
export class AuthEffects {

  private userEmail = GUEST_USER;
  private smartlookKeyDecrypted: Promise<string>;

  constructor(
    @Inject(HOME_DIRECTORY) private readonly homeDirectory: string,
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly authFacade: AuthFacade,
    private readonly userAPIService: UserAPIService,
    private readonly jurisdictionAPIService:JurisdictionAPIService,
    private readonly toteAccountAPIService: ToteAccountAPIService,
    private readonly sessionService: SessionService,
    private readonly pusherService: PusherService,
    private readonly commonFacade: CommonFacade,
    private readonly wagerFacade: WagerFacade,
    private readonly accountFacade: AccountFacade,
    private readonly fundingFacade: FundingFacade,
    private readonly ticketFacade: TicketFacade,
    private readonly platform: Platform,
    private readonly ngZone: NgZone,
    private readonly smartlook: Smartlook,
    @Inject(SMARTLOOK) readonly smartlookKey: string,
  ) {
    this.smartlookKeyDecrypted = TO_DECRYPT(this.smartlookKey);
  }

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initAuthState),
      fetch({
        id: () => 'init-auth',
        run: () => AuthActions.loadAuthStateSuccess(),
        // run: () => this.userAPIService.guest$()
        //   .pipe(
        //     map(({ Model: response }) => {
        //       console.log('guest:',response);

        //       // cookies
        //       // this.sessionService.storeCookiesLoginResponse(
        //       //   response as Partial<ILoggedInUserModel>,
        //       //   'guest'
        //       // );

        //       return AuthActions.loadAuthStateSuccess({ data: response})
        //     })
        //   )
        // ,
        /* eslint-disable no-empty-pattern */
        onError: ({}, error) => AuthActions.loadAuthStateFailure({ error }),
      })
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loggingInAPI),
      fetch({
        id: () => 'login',
        run: ({ username, password, tmsId, from, talon }) => this.userAPIService.login$({
          Username: username,
          Password: password,
          Tmsid: tmsId,
          TalonPayload: talon
        })
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
            // map(({ Model: response }) => {

            try {
              // eslint-disable-next-line no-unsafe-optional-chaining
              if ("Model" in response) {
                response = response?.Model;
              }
            } catch (error) {
              console.error("Model:API");
            }

              // set as error if no token
              if (!response?.Token) return AuthActions.loginAPIFailed({
                errorResponse: response
              });

              this.platform.ready().then(() => {
                // console.log('Smartlook-1:', this.smartlookKey);

                this.ngZone.run(async () => {
                  // TODO: only for BETA testing, clarify to Jason for Official Release
                  if (this.smartlookKey) {
                    if (
                        this.sessionService?.hasToken()
                        && (this.sessionService?.getUserEmail() !== GUEST)
                    ) {
                      this.smartlook.setProjectKey({ key: await this.smartlookKeyDecrypted });
                      this.smartlook.start();
                      this.smartlook.setUserName({
                        name: this.sessionService?.getUserFName() + ' ' + this.sessionService?.getUserLast()
                      });
                      this.smartlook.setUserEmail({ email: this.sessionService?.getUserEmail() });
                      this.userEmail = this.sessionService.getUserEmail();
                    }
                    else {
                      this.smartlook.openNewUser();
                      this.smartlook.setUserName({ name: response?.FirstName + ' ' + response?.LastName });
                      this.smartlook.setUserEmail({ email: username });
                      this.userEmail = username;
                    }
                    // console.log('Smartlook-2:', this.smartlookKey);
                  }
                });
              });

              // clear guest-user

              // ngrx state property
              this.authFacade.setIsAuthenticated(true);

              // cookies
              this.sessionService.storeCookiesLoginResponse(response, username);
              this.authFacade.isRememberMe$()
                .pipe(take(1))
                .subscribe((isRememberMe: boolean | undefined ) => {

                  this.sessionService.setIsRememberEmail(
                    username,
                    this.sessionService.getOptions('/', 30)
                  );

                  this.sessionService.setIsRememberMe(
                    isRememberMe ? 'true' : 'false',
                    this.sessionService.getOptions('/', 30)
                  );
              });

              // pusher
              this.pusherService.unSubscribeCustomer();
              this.pusherService.subscribeCustomer();

              // this.commonFacade.setIsShowNavigation(true);
              this.commonFacade.setIsAuthenticated(true, "AuthEffect:login");

              //set fullname
              this.authFacade.setUserFullName(
                response.FirstName + ' ' + response.LastName
              );

              // tmsid
              this.commonFacade.setTmsId(this.sessionService.generateSessionId());

              // get balance
              this.accountFacade.getBalance("AuthEffects:login");

              // get config
              this.accountFacade.getAccountPreferencesAPI();

              // get country states
              this.authFacade.jurisdictions('US');

              // redirect to home page
              // this.router.navigate([this.homeDirectory]);

              //add return in each condition to avoid random redirects
              const action = AuthActions.loginAPISuccess({
                successResponse: response,
                username
              });

              // after signup -> login -> complete registration
              if (from === 'complete-registration') {
                this.router.navigate([from]);
                return action;
              } else if (response.RoleId === 9) {
                this.router.navigate(['/complete-registration']);
                return action;
              } else if (response.RoleId === 11) {
                this.router.navigate(['/documents-verification']);
                return action;
              } else {

                this.wagerFacade.getEventsDetails("AuthEffect:login");

                this.commonFacade.setIsShowNavigation(true);
                this.commonFacade.setIsShowFooter(true);
                this.wagerFacade.setIsShowBetPreview(false);
                this.router.navigate([this.homeDirectory]);
                return action;
              }
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AuthActions.loginAPIFailed({ errorResponse });
        }
      })
    )
  );

  loginGuest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loggingGuestAPI),
      fetch({
        id: () => 'login-guest',
        run: () => this.userAPIService.guest$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              // console.log('guest:',response);

              this.platform.ready().then(() => {
                // console.log('Smartlook-1:', this.smartlookKey);
                this.ngZone.run(async () => {
                  // TODO: only for BETA testing, clarify to Jason for Official Release
                  if (this.smartlookKey && this.userEmail === GUEST_USER) {
                    this.smartlook.setProjectKey({ key: await this.smartlookKeyDecrypted });
                    this.smartlook.start();
                    this.smartlook.setUserName({ name: GUEST_USER });
                    this.smartlook.setUserEmail({ email: GUEST_USER });
                    // console.log('Smartlook-2:', this.smartlookKey);
                  }
                });
              });

              // cookies
              this.sessionService.storeCookiesLoginResponse(
                response as Partial<ILoggedInUserModel>,
                GUEST
              );

              // pusher
              this.pusherService.unSubscribeCustomer();
              this.pusherService.subscribeCustomer();

              this.wagerFacade.getEventsDetails("AuthEffect:loginGuest");

              return AuthActions.loginAPISuccess({
                successResponse: response,
                username: GUEST
              })
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AuthActions.loginAPIFailed({ errorResponse });
        }
      })
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loggingOutAPI),
      fetch({
        id: () => 'logout',
        run: ({ from }) => this.userAPIService.logout$()
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              // pusher
              this.pusherService.unSubscribeCustomer();

              // cleanup storage
              this.sessionService.resetLogout();

              // set new guest-user

              // Todo: remove, for wager (open to guest user)
              // pusher
              // this.pusherService.disconnect();

              this.commonFacade.setIsAuthenticated(false, "AuthEffect:logout");
              this.wagerFacade.setIsShowBetPreview(false);

              // don't delete and comment
              this.authFacade.loginGuest();

              if (from === 'change-password') {
                this.sessionService.setChangePasswordIsActive(
                  'true', this.sessionService.getOptions('/', 30)
                );
                // redirect to forgot password screen
                this.router.navigate(['/forgot-password']);
              }
              else if (from === INACTIVE) {
                // redirect to login screen
                this.router.navigate(['/login']);

                // exit app to stop background processes such as Pusher, API request, and observables
                // ...that cause battery life and to slow phone
                setTimeout(() =>
                  App.exitApp(),
                  3000 // execute after 3 seconds
                );
              }
              else {
                // redirect to login screen
                this.router.navigate(['/login']);
              }

              // clear the state manangement
              this.accountFacade.clearState('AuthEffects:logout');
              this.authFacade.clearState('AuthEffects:logout');
              this.fundingFacade.clearState('AuthEffects:logout');
              this.ticketFacade.clearState('AuthEffects:logout');
              this.wagerFacade.clearState('AuthEffects:logout');
              this.commonFacade.clearState('AuthEffects:logout');

              // const username = this.sessionService.getUserEmail();

              // cookies
              // this.sessionService.storeCookiesLoginResponse(response, username);

              // this.wagerFacade.getEventsDetails("AuthEffect:logout");

              return AuthActions.logoutAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: ({}, errorResponse) => AuthActions.logoutAPIFailed({ errorResponse }),
      })
    )
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerAPI),
      fetch({
        id: () => 'register',
        run: ({ username, password, tmsId, talon, fingerprint }) => this.userAPIService.signup$({
          Email: username,
          Password: password,
          Tmsid: tmsId,
          TalonPayload: talon,
          FPJS: fingerprint
        })
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              // remove since there's already an auto login after signup, login effect will call complete-registration screen
              // by calling this method it will call and show Complete Registration screen x2
              // this.router.navigate(['/complete-registration']);
              // currently it showing a blank screen if the method is remove, so added wager screen
              this.router.navigate(['/wager']);

              return AuthActions.registerAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AuthActions.registerAPIFailed({ errorResponse });
        }
      })
    )
  );

  completeRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.completeRegistrationAPI),
      fetch({
        id: () => 'verifiy-identity',
        run: ({
            firstName,
            lastName,
            dateOfBirth,
            streetAddress,
            city,
            state,
            zipCode,
            phoneNumber,
            ssn,
            tmsId,
            talon
        }) => this.userAPIService.verifiyIdentity$({
          FirstName: firstName,
          LastName: lastName,
          DateOfBirth: dateOfBirth,
          Address: streetAddress,
          City: city,
          State: state,
          ZipCode: zipCode,
          Country: 'US',
          PhoneNo: phoneNumber,
          Last4SSN: ssn,
          Tmsid: tmsId,
          TalonPayload: talon
        })
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              const options1day: Web.ICookie = this.sessionService.getOptions('/', 1);
              this.sessionService.setRoleId(7, options1day);
              this.router.navigate(['/wager']);

              return AuthActions.completeRegistrationAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          // const options1day: Web.ICookie = this.sessionService.getOptions('/', 1);
          // this.sessionService.setRoleId(11, options1day);
          this.router.navigate(['/complete-registration']);
          return AuthActions.completeRegistrationAPIFailed({ errorResponse });
        }
      })
    )
  );

  uploadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.uploadDocumentsAPI),
      fetch({
        id: () => 'documents-verification',
        run: ({ customerId, idDocuments, billDocuments }) => this.userAPIService.uploadDocuments$({
          CustomerId: customerId,
          Base64IdDoc: idDocuments,
          Base64AddressDoc: billDocuments,
        })
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              // if (response.ErrorCode || response.Message) {
              //   return AuthActions.uploadDocumentsAPIFailed({ errorResponse: response })
              // }

              this.router.navigate(['/wager']);

              return AuthActions.uploadDocumentsAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          this.router.navigate(['/documents-verification']);
          return AuthActions.uploadDocumentsAPIFailed({ errorResponse });
        }
      })
    )
  );

  renewPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.renewPasswordAPI),
      fetch({
        id: () => 'forgot-password',
        run: ({ email }) => this.toteAccountAPIService.renewPasswordLink$(email)
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              this.router.navigate(['/success-request-reset-password']);

              return AuthActions.renewPasswordAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AuthActions.renewPasswordAPIFailed({ errorResponse });
        }
      })
    )
  );

  updatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.updatePasswordAPI),
      fetch({
        id: () => 'update-password',
        run: ({ newPassword, replaceIdentifier }) => this.toteAccountAPIService.updatePassword$({
          ReplaceIdentifier: replaceIdentifier,
          NewPassword: newPassword,
        })
          .pipe(
            // map(({ Model: response }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

              try {
                // eslint-disable-next-line no-unsafe-optional-chaining
                if ("Model" in response) {
                  response = response?.Model;
                }
              } catch (error) {
                console.error("Model:API");
              }

              this.router.navigate(['/success-update-password']);

              return AuthActions.updatePasswordAPISuccess({ successResponse: response });
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AuthActions.updatePasswordAPIFailed({ errorResponse });
        }
      })
    )
  );

  jurisdections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.jurisdictionsAPI),
      fetch({
        id: () => 'jurisdections',
        run: () => this.jurisdictionAPIService.getJurisdictions$()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            map((response: any) => {
              // map(({ Model: response }) => {

                try {
                  // eslint-disable-next-line no-unsafe-optional-chaining
                  if ("Model" in response) {
                    response = response?.Model;
                  }
                } catch (error) {
                  console.error("Model:API");
                }

                let states = [];
                if (response.US !== undefined) {
                  // this.authFacade.setCountryStates(response.US.Subdivisions);
                  states = response.US.Subdivisions;
                }
                else {
                  // this.authFacade.setCountryStates([]);
                  states = [];
                }

              return AuthActions.jurisdictionsAPISuccess({
                successResponse: states
              })
            })
          )
        ,
        onError: (param, errorResponse) => {
          return AuthActions.jurisdictionsAPIFailed({ errorResponse });
        }
      })
    )
  );

}
